import { Defect, Datum } from "@/api/welds";
import { faXmarkCircle, faCommentDots } from "@fortawesome/free-regular-svg-icons";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CommentsDialog } from "../../../common/Dialogs";
import { GenericDialog } from "@/components/dialogs";
import { InspectorRow } from "@/api/hitachi/rbi";
import { useCoordinatorMenu } from "@/queries/menu.queries";
import { useDefectTypeMenu } from "../hooks";
import { useState } from "react";
import { useModal } from "@/hooks/useModal";
import { v4 as uuid } from "uuid";

import styles from "./DefectSelection.tsx.module.css";

function isNumericDatumDistance(value?: string) {
  if (!value?.trim()) {
    return true;
  }

  return !Number.isNaN(Number(value));
}

export type DefectSelectionProps = {
  weld: InspectorRow;
  caption: string;
  onAccept: (defects: Defect[]) => void;
};

export function DefectSelection(props: Readonly<DefectSelectionProps>) {
  const [defects, setDefects] = useState<Defect[]>(
    props.weld.defects.length > 0 ? props.weld.defects : [{ defectId: uuid(), defectNumber: getDefectNumber(0) }]
  );
  const [validationError, setValidationError] = useState<string>("");

  const defectTypeMenu = useDefectTypeMenu(props.weld.discipline);
  const defectLevelMenu = useCoordinatorMenu("DefectLevel");
  const severityLevelMenu = useCoordinatorMenu("DefectSeverity");
  const modal = useModal();

  function handleAdd() {
    setDefects((prev) => [...prev, { defectId: uuid(), defectNumber: getDefectNumber(defects.length) }]);
  }

  function handleRemove(defect: Defect) {
    const newDefects = defects.filter((x) => x !== defect);
    // Re-assign defect numbers.
    for (let i = 0; i < newDefects.length; i++) {
      newDefects[i].defectNumber = getDefectNumber(i);
    }

    if (newDefects.length > 0) {
      setDefects(newDefects);
    } else {
      // Ensure there is always at least one row
      setDefects([{ defectId: uuid(), defectNumber: getDefectNumber(0) }]);
    }
  }

  function handleFieldChanged(index: number, field: keyof Defect, value?: string | File) {
    const newDefects = [...defects];
    newDefects[index] = { ...newDefects[index], [field]: value };
    setDefects(newDefects);
    if (validationError) {
      setValidationError("");
    }
  }

  function handleDatumChanged(defectIndex: number, datumIndex: number, field: keyof Datum, value: string) {
    const newDefects = [...defects];
    const newDefect = { ...newDefects[defectIndex] };
    const datums = newDefect.datums || [];
    const newDatum = { ...datums[datumIndex], [field]: value };
    newDatum[field] = value;
    newDatum.dimension = ["x", "y", "z"][datumIndex];
    newDefects[defectIndex] = {
      ...newDefect,
      datums: [...datums.slice(0, datumIndex), newDatum, ...datums.slice(datumIndex + 1)],
    };
    setDefects(newDefects);
    if (validationError) {
      setValidationError("");
    }
  }

  function getDefectNumber(index: number) {
    const weld = props.weld;
    const offset = weld.repairCount + 1;

    if (weld.discipline === "UT") {
      return `${index + 1}`;
    } else if (weld.discipline.startsWith("PMI")) {
      return `${index + 1}`;
    } else if (weld.weldRepairLevel === 0) {
      return `${weld.weldNumber}-R${index + offset}`;
    } else if (weld.weldRepairLevel === 1) {
      return `${weld.weldNumber}-R1`;
    } else if (weld.weldRepairLevel === 2 && weld.weldNumber.endsWith("-R1")) {
      return `${weld.weldNumber.slice(0, -3)}-R2`;
    } else if (weld.weldRepairLevel === 3 && weld.weldNumber.endsWith("-R2")) {
      return `${index + 1}`; //return `${weld.weldNumber.slice(0, weld.weldNumber.indexOf("-R"))}-RW`;
    }
  }
  function handleSave(): boolean {
    const defectsWithType = defects.filter((defect) => defect.defectType);
    const missingComments = defectsWithType.some((d) => !d.defectNotes?.trim());
    const hasInvalidDatumDistance = defectsWithType.some((defect) =>
      defect.datums?.some((datum) => !isNumericDatumDistance(datum.distance))
    );

    if (missingComments) {
      setValidationError("Please enter a Comment for all defects.");
      return false;
    }

    if (hasInvalidDatumDistance) {
      setValidationError("Please enter numeric values only for datum distances.");
      return false;
    }

    setValidationError("");
    props.onAccept(defectsWithType);
    return true;
  }

  return (
    <GenericDialog
      caption={props.caption ? props.caption : "defects"}
      buttons="OKCancel"
      onAccept={() => handleSave()}
      className={styles.defectSelection}
    >
      {validationError ? (
        <div className="alert alert-danger" role="alert">
          {validationError}
        </div>
      ) : (
        <></>
      )}
      <table className="table table-sm table-borderless">
        <thead>
          <tr>
            <th>Defect Number</th>
            <th>Description</th>
            <th>Length Category</th>
            <th>Severity Category</th>
            <th>Length (mm)</th>
            <th> </th>
            <th>Datums</th>
            <th>Distance from Datum (mm)</th>
            {(props.weld.discipline === "UT" || props.weld.discipline === "PMIUT") && (
              <>
                <th>Best Response Probe</th>
                <th>Estimated Through Wall Thickness (mm)</th>
                <th>Probe Location from Centre Weld Axis (mm)</th>
                <th>Maximum Echo Amplitude (dB)</th>
              </>
            )}
            <th>Image</th>
            <th>Comment</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {defects.map((defect, index) => (
            // Include defectType so removing the last defect works correctly.
            <tr key={`${index}${defect.defectType}`}>
              <td>
                <input
                  type="text"
                  className="form-control"
                  disabled
                  value={defect.defectNumber ?? ""}
                  onChange={(e) => handleFieldChanged(index, "defectNumber", e.currentTarget.value)}
                />
              </td>
              <td>
                <select
                  className="form-select"
                  value={defect.defectType ?? ""}
                  onChange={(e) => handleFieldChanged(index, "defectType", e.currentTarget.value)}
                  disabled={defectTypeMenu.isLoading}
                >
                  <option />
                  {defectTypeMenu.data?.map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  className="form-select"
                  value={defect.defectLevel ?? ""}
                  onChange={(e) => handleFieldChanged(index, "defectLevel", e.currentTarget.value)}
                  disabled={defectLevelMenu.isLoading}
                >
                  <option />
                  {defectLevelMenu.data?.map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  className="form-select"
                  value={defect.defectSeverity ?? ""}
                  onChange={(e) => handleFieldChanged(index, "defectSeverity", e.currentTarget.value)}
                  disabled={severityLevelMenu.isLoading}
                >
                  <option />
                  {severityLevelMenu.data?.map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="text"
                  className="form-control"
                  value={defect.defectLength ?? ""}
                  onChange={(e) => handleFieldChanged(index, "defectLength", e.currentTarget.value)}
                />
              </td>
              <td>
                <div>
                  <label>X</label>
                </div>
                <div>
                  <label>Y</label>
                </div>
                <div>
                  <label>Z</label>
                </div>
              </td>
              <td>
                {[0, 1, 2].map((datumIndex) => (
                  <input
                    key={`desc-${datumIndex}`}
                    type="text"
                    value={defect.datums?.[datumIndex]?.description || ""}
                    className="form-control form-control-sm"
                    onChange={(e) => handleDatumChanged(index, datumIndex, "description", e.currentTarget.value)}
                  />
                ))}
              </td>
              <td>
                {[0, 1, 2].map((datumIndex) => (
                  <input
                    key={`distance-${datumIndex}`}
                    type="text"
                    value={defect.datums?.[datumIndex]?.distance || ""}
                    inputMode="decimal"
                    className={`form-control form-control-sm ${
                      isNumericDatumDistance(defect.datums?.[datumIndex]?.distance) ? "" : "is-invalid"
                    }`}
                    onChange={(e) => handleDatumChanged(index, datumIndex, "distance", e.currentTarget.value)}
                  />
                ))}
              </td>
              {(props.weld.discipline === "UT" || props.weld.discipline === "PMIUT") && (
                <>
                  <td>
                    <select
                      className="form-select"
                      value={defect.bestResponseProbe ?? ""}
                      onChange={(e) => handleFieldChanged(index, "bestResponseProbe", e.currentTarget.value)}
                    >
                      <option />
                      {props.weld.probes.map((value) => (
                        <option key={value.serialNumber}>{value.serialNumber}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={defect.estimatedWallThickness ?? ""}
                      onChange={(e) => handleFieldChanged(index, "estimatedWallThickness", e.currentTarget.value)}
                    />
                  </td>

                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={defect.probeLocation ?? ""}
                      onChange={(e) => handleFieldChanged(index, "probeLocation", e.currentTarget.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={defect.maxEchoAmplitude ?? ""}
                      onChange={(e) => handleFieldChanged(index, "maxEchoAmplitude", e.currentTarget.value)}
                    />
                  </td>
                </>
              )}
              <td className="text-center align-middle">
                <label htmlFor={`attachment${index}`}>
                  <FontAwesomeIcon icon={faCamera} role="button" title="Select" />{" "}
                  {defect.attachment && <span>+1</span>}
                </label>
                <input
                  type="file"
                  id={`attachment${index}`}
                  className="d-none"
                  onChange={(e) =>
                    e.currentTarget.files && handleFieldChanged(index, "attachment", e.currentTarget.files[0])
                  }
                />
              </td>
              <td className="text-center align-middle">
                <a
                  role="button"
                  title="Comments"
                  onClick={() =>
                    modal.show(
                      <CommentsDialog
                        comment={defect.defectNotes || ""}
                        onAccept={(comment: string) => handleFieldChanged(index, "defectNotes", comment)}
                      />
                    )
                  }
                >
                  <FontAwesomeIcon icon={faCommentDots} /> {defect.defectNotes && <span>+1</span>}
                </a>
              </td>
              <td className="align-middle">
                <FontAwesomeIcon
                  icon={faXmarkCircle}
                  role="button"
                  title="Delete"
                  onClick={() => handleRemove(defect)}
                />
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={4}>
              <button className="btn btn-link p-0" onClick={handleAdd}>
                Add Defect
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </GenericDialog>
  );
}
